import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { tokenStore } from '../../lib/tokenStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import {
  Download, Upload, FileText, Grid3X3, Hash, Ruler,
  Play, RotateCcw, Maximize2, Monitor, Zap, Weight
} from 'lucide-react';

// ─── Pattern drawing functions ──────────────────────────────────
const drawSolidColor = (ctx, w, h, primary) => {
  ctx.fillStyle = primary;
  ctx.fillRect(0, 0, w, h);
};

const drawHorizontalGradient = (ctx, w, h, primary, secondary) => {
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  grad.addColorStop(0, secondary);
  grad.addColorStop(1, primary);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
};

const drawVerticalGradient = (ctx, w, h, primary, secondary) => {
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, primary);
  grad.addColorStop(1, secondary);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
};

const drawCheckerboard = (ctx, w, h, primary, secondary, cols, rows) => {
  const cw = w / cols, ch = h / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = (r + c) % 2 === 0 ? primary : secondary;
      ctx.fillRect(c * cw, r * ch, cw, ch);
    }
  }
};

const drawColorBars = (ctx, w, h) => {
  const colors = ['#ffffff', '#ffff00', '#00ffff', '#00ff00', '#ff00ff', '#ff0000', '#0000ff', '#000000'];
  const bw = w / colors.length;
  colors.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(i * bw, 0, bw, h); });
};

const drawGridLines = (ctx, w, h, primary, cols, rows) => {
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = primary;
  ctx.lineWidth = 1;
  const cw = w / cols, ch = h / rows;
  for (let c = 0; c <= cols; c++) { ctx.beginPath(); ctx.moveTo(c * cw, 0); ctx.lineTo(c * cw, h); ctx.stroke(); }
  for (let r = 0; r <= rows; r++) { ctx.beginPath(); ctx.moveTo(0, r * ch); ctx.lineTo(w, r * ch); ctx.stroke(); }
};

const drawCrosshatch = (ctx, w, h, primary) => {
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = primary;
  ctx.lineWidth = 1;
  const step = 20;
  for (let i = -h; i < w; i += step) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + h, h); ctx.stroke(); }
  for (let i = -h; i < w; i += step) { ctx.beginPath(); ctx.moveTo(i + h, 0); ctx.lineTo(i, h); ctx.stroke(); }
};

const drawFocusPattern = (ctx, w, h, primary) => {
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = primary;
  ctx.lineWidth = 1;
  const cx = w / 2, cy = h / 2;
  const maxR = Math.min(w, h) / 2;
  for (let r = maxR / 8; r <= maxR; r += maxR / 8) {
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(w, cy); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, h); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(w, h); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(w, 0); ctx.lineTo(0, h); ctx.stroke();
};

const drawRgbTest = (ctx, w, h, cols, rows) => {
  const cw = w / cols, ch = h / rows;
  const third = Math.ceil(cols / 3);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (c < third) ctx.fillStyle = '#ff0000';
      else if (c < third * 2) ctx.fillStyle = '#00ff00';
      else ctx.fillStyle = '#0000ff';
      ctx.fillRect(c * cw, r * ch, cw, ch);
    }
  }
};

const drawGrayscale = (ctx, w, h, cols, rows) => {
  const cw = w / cols, ch = h / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const v = Math.round((c / (cols - 1 || 1)) * 255);
      ctx.fillStyle = `rgb(${v},${v},${v})`;
      ctx.fillRect(c * cw, r * ch, cw, ch);
    }
  }
};

const drawWhite = (ctx, w, h) => { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); };
const drawBlack = (ctx, w, h) => { ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, w, h); };

// ─── Pattern list ───────────────────────────────────────────────
const PATTERNS = [
  { id: 'solid', label: 'Solid Color', desc: 'Single color fill' },
  { id: 'h_gradient', label: 'Horizontal Gradient', desc: 'Left to right blend' },
  { id: 'v_gradient', label: 'Vertical Gradient', desc: 'Top to bottom blend' },
  { id: 'checkerboard', label: 'Checkerboard', desc: 'Alternating squares' },
  { id: 'color_bars', label: 'Color Bars', desc: 'SMPTE-style bars' },
  { id: 'grid_lines', label: 'Grid Lines', desc: 'Panel alignment grid' },
  { id: 'crosshatch', label: 'Crosshatch', desc: 'Diagonal lines' },
  { id: 'focus', label: 'Focus Pattern', desc: 'Circles and crosses' },
  { id: 'rgb_test', label: 'RGB Test', desc: 'Red, Green, Blue zones' },
  { id: 'grayscale', label: 'Grayscale', desc: 'Black to white steps' },
  { id: 'white', label: 'White (100%)', desc: 'Full white for brightness' },
  { id: 'black', label: 'Black (0%)', desc: 'Dead pixel detection' },
];

// ─── Main Component ─────────────────────────────────────────────
const LedTestPattern = () => {
  const { sceneObjects, selectedObjectId, updateSceneObject, worldSettings, setWorldSettings } = useApp();
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const logoInputRef = useRef(null);

  // Find LED screens in scene
  const ledScreens = useMemo(() => sceneObjects.filter(o => o.type === 'led_screen'), [sceneObjects]);
  const [activeLedId, setActiveLedId] = useState(null);

  // Auto-select LED wall when user clicks it in 3D viewport
  useEffect(() => {
    const sel = sceneObjects.find(o => o.id === selectedObjectId);
    if (sel?.type === 'led_screen') {
      setActiveLedId(sel.id);
    }
  }, [selectedObjectId, sceneObjects]);

  // Track which LED wall is active for editing
  const activeLed = useMemo(() => {
    if (activeLedId) {
      const found = ledScreens.find(o => o.id === activeLedId);
      if (found) return found;
    }
    // Fall back to selected object or first LED
    const sel = sceneObjects.find(o => o.id === selectedObjectId);
    if (sel?.type === 'led_screen') return sel;
    return ledScreens[0] || null;
  }, [sceneObjects, selectedObjectId, ledScreens, activeLedId]);

  const led = activeLed?.ledConfig || {};
  const ledMode = led.ledMode || 'standard';
  const baseCols = led.columns || 4;
  const rows = led.rows || 1;
  const tileW = led.tileWidth || 500;
  const tileH = led.tileHeight || 500;
  const tileResH = led.tileResH || 128;
  const tileResV = led.tileResV || 128;

  // Compute effective layout based on LED mode
  const layout = useMemo(() => {
    const isCorner = ledMode === 'corner';
    const isSoftCorner = ledMode === 'soft_corner';
    const leftCols = Math.max(1, led.leftColumns || Math.ceil(baseCols / 2));
    const rightCols = Math.max(1, led.rightColumns || Math.floor(baseCols / 2));

    // Build column widths array (in pixels)
    let colWidths = []; // pixel width of each logical column
    let colPhysWidths = []; // physical width in mm

    if (isCorner) {
      // Left tiles + Right tiles (all same width)
      for (let i = 0; i < leftCols + rightCols; i++) { colWidths.push(tileResH); colPhysWidths.push(tileW); }
    } else if (isSoftCorner) {
      // Left tiles + corner ribbon (125mm) + Right tiles
      for (let i = 0; i < leftCols; i++) { colWidths.push(tileResH); colPhysWidths.push(tileW); }
      const cornerPx = Math.max(1, Math.round(125 / tileW * tileResH));
      colWidths.push(cornerPx); colPhysWidths.push(125);
      for (let i = 0; i < rightCols; i++) { colWidths.push(tileResH); colPhysWidths.push(tileW); }
    } else {
      // Standard / curved / flexible
      if (led.softCornerLeft) {
        const cornerPx = Math.max(1, Math.round(125 / tileW * tileResH));
        colWidths.push(cornerPx); colPhysWidths.push(125);
      }
      for (let i = 0; i < baseCols; i++) { colWidths.push(tileResH); colPhysWidths.push(tileW); }
      if (led.softCornerRight) {
        const cornerPx = Math.max(1, Math.round(125 / tileW * tileResH));
        colWidths.push(cornerPx); colPhysWidths.push(125);
      }
    }

    const totalCols = colWidths.length;
    const totalResW = colWidths.reduce((a, b) => a + b, 0);
    const totalResHFull = tileResV * rows;
    const totalPhysW = colPhysWidths.reduce((a, b) => a + b, 0);
    const totalPhysH = tileH * rows;
    const flatTiles = isCorner ? (leftCols + rightCols) : isSoftCorner ? (leftCols + rightCols) : baseCols;
    const totalPanels = flatTiles * rows;

    // Cumulative X positions (in pixels) for grid drawing
    const colXPositions = [0];
    for (let i = 0; i < colWidths.length; i++) {
      colXPositions.push(colXPositions[i] + colWidths[i]);
    }

    return { colWidths, colPhysWidths, colXPositions, totalCols, totalResW, totalResH: totalResHFull, totalPhysW, totalPhysH, totalPanels, flatTiles };
  }, [ledMode, led.leftColumns, led.rightColumns, baseCols, rows, tileW, tileH, tileResH, tileResV, led.softCornerLeft, led.softCornerRight]);

  const cols = layout.totalCols;
  const totalResW = layout.totalResW;
  const totalResH = layout.totalResH;
  const physW = layout.totalPhysW / 1000;
  const physH = layout.totalPhysH / 1000;
  const removedCount = (led.removedTiles || []).length;
  const totalPanels = layout.totalPanels - removedCount;
  const powerPerPanel = led.powerPerTile || 200;
  const weightPerPanel = led.weightPerTile || 12;

  // Per-wall settings map: { wallId: { pattern, primaryColor, secondaryColor, borderWidth, showGrid, showNumbers, showResolution } }
  const defaultSettings = { pattern: 'solid', primaryColor: '#1aa392', secondaryColor: '#0a0a0a', borderWidth: 2, showGrid: true, showNumbers: true, showResolution: true, labelColor: '#ffffff', labelSize: 50, labelOffsetX: 0, labelOffsetY: 0 };
  const [wallSettings, setWallSettings] = useState({});
  const [logoImage, setLogoImage] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const settingsSyncTimerRef = useRef(null);
  const initializedWallsRef = useRef(new Set());

  const wallId = activeLed?.id;
  const settings = wallId ? (wallSettings[wallId] || defaultSettings) : defaultSettings;

  // Initialize wall settings from persisted ledConfig.testPattern when a wall is first selected
  useEffect(() => {
    if (!wallId) return;
    if (initializedWallsRef.current.has(wallId)) return;
    const persisted = activeLed?.ledConfig?.testPattern;
    if (persisted) {
      setWallSettings(prev => ({
        ...prev,
        [wallId]: { ...defaultSettings, ...persisted }
      }));
    }
    initializedWallsRef.current.add(wallId);
  }, [wallId, activeLed]);

  // Initialize logo from worldSettings on mount
  const logoInitializedRef = useRef(false);
  useEffect(() => {
    if (logoInitializedRef.current) return;
    const savedLogo = worldSettings?.testPatternLogo;
    if (savedLogo) {
      logoInitializedRef.current = true;
      setLogoDataUrl(savedLogo);
      const img = new Image();
      img.onload = () => setLogoImage(img);
      img.src = savedLogo;
    }
  }, [worldSettings?.testPatternLogo]);

  // Debounced sync of wall settings to scene object's ledConfig.testPattern
  const syncSettingsToScene = useCallback((wId, newSettings) => {
    if (settingsSyncTimerRef.current) clearTimeout(settingsSyncTimerRef.current);
    settingsSyncTimerRef.current = setTimeout(() => {
      const obj = sceneObjects.find(o => o.id === wId);
      if (obj) {
        updateSceneObject(wId, {
          ledConfig: { ...obj.ledConfig, testPattern: newSettings }
        });
      }
    }, 1000);
  }, [sceneObjects, updateSceneObject]);

  const updateSetting = (key, value) => {
    if (!wallId) return;
    const newSettings = { ...(wallSettings[wallId] || defaultSettings), [key]: value };
    setWallSettings(prev => ({ ...prev, [wallId]: newSettings }));
    syncSettingsToScene(wallId, newSettings);
  };

  // Convenience accessors for current wall
  const pattern = settings.pattern;
  const primaryColor = settings.primaryColor;
  const secondaryColor = settings.secondaryColor;
  const borderWidth = settings.borderWidth;
  const showGrid = settings.showGrid;
  const showNumbers = settings.showNumbers;
  const showResolution = settings.showResolution;
  const labelColor = settings.labelColor;
  const labelSize = settings.labelSize;
  const labelOffsetX = settings.labelOffsetX;
  const labelOffsetY = settings.labelOffsetY;

  // ─── Draw the canvas ──────────────────────────────────
  const drawCanvas = useCallback((targetCanvas, scale = 1) => {
    if (!targetCanvas) return;
    const w = totalResW * scale;
    const h = totalResH * scale;
    targetCanvas.width = w;
    targetCanvas.height = h;
    const ctx = targetCanvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    const ch = h / rows;
    // Scaled column X positions and widths
    const colXScaled = layout.colXPositions.map(x => x * scale);
    const colWScaled = layout.colWidths.map(cw => cw * scale);

    // Draw pattern background (uses uniform cols for patterns that need grid)
    switch (pattern) {
      case 'solid': drawSolidColor(ctx, w, h, primaryColor); break;
      case 'h_gradient': drawHorizontalGradient(ctx, w, h, primaryColor, secondaryColor); break;
      case 'v_gradient': drawVerticalGradient(ctx, w, h, primaryColor, secondaryColor); break;
      case 'checkerboard': drawCheckerboard(ctx, w, h, primaryColor, secondaryColor, cols, rows); break;
      case 'color_bars': drawColorBars(ctx, w, h); break;
      case 'grid_lines': drawGridLines(ctx, w, h, primaryColor, cols, rows); break;
      case 'crosshatch': drawCrosshatch(ctx, w, h, primaryColor); break;
      case 'focus': drawFocusPattern(ctx, w, h, primaryColor); break;
      case 'rgb_test': drawRgbTest(ctx, w, h, cols, rows); break;
      case 'grayscale': drawGrayscale(ctx, w, h, cols, rows); break;
      case 'white': drawWhite(ctx, w, h); break;
      case 'black': drawBlack(ctx, w, h); break;
      default: drawSolidColor(ctx, w, h, primaryColor);
    }

    // Logo overlay
    if (logoImage) {
      const lw = w * 0.3;
      const lh = (logoImage.height / logoImage.width) * lw;
      ctx.globalAlpha = 0.7;
      ctx.drawImage(logoImage, (w - lw) / 2, (h - lh) / 2, lw, lh);
      ctx.globalAlpha = 1;
    }

    // Grid lines using actual column positions (variable widths)
    if (showGrid && borderWidth > 0) {
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = borderWidth * scale;
      for (let c = 0; c <= cols; c++) {
        const x = colXScaled[c];
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let r = 0; r <= rows; r++) {
        ctx.beginPath(); ctx.moveTo(0, r * ch); ctx.lineTo(w, r * ch); ctx.stroke();
      }
    }

    // Panel numbers (variable column widths)
    if (showNumbers) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cw = colWScaled[c];
          const cx = colXScaled[c] + cw / 2;
          const cy = r * ch + ch / 2;
          const fontSize = Math.max(12, Math.min(cw, ch) * 0.3) * scale;
          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#ffffff';
          ctx.shadowColor = 'rgba(0,0,0,0.7)';
          ctx.shadowBlur = 4 * scale;
          const num = r * cols + c + 1;
          ctx.fillText(String(num), cx, cy);
        }
      }
      ctx.shadowBlur = 0;
    }

    // Resolution labels (variable column widths)
    if (showResolution) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cw = colWScaled[c];
          const cx = colXScaled[c] + cw / 2;
          const colResPx = layout.colWidths[c];
          const rFontSize = Math.max(8, Math.min(cw, ch) * 0.12) * scale;
          ctx.font = `${rFontSize}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.fillText(`${colResPx}x${tileResV}`, cx, r * ch + 4 * scale);
        }
      }
    }

    // Center label: LED Screen name + Total resolution (large, centered)
    const screenName = activeLed?.name || 'LED Screen';
    const resText = `${totalResW} x ${totalResH}`;
    const sizeF = (labelSize / 50); // 50 = default, so 1x scale
    const centerFontSize = Math.max(16, Math.min(w, h) * 0.08 * sizeF) * scale;
    const nameFontSize = Math.max(12, centerFontSize * 0.65);
    const cx = w / 2 + (labelOffsetX / 100) * w * scale;
    const cy = h / 2 + (labelOffsetY / 100) * h * scale;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 6 * scale;
    ctx.fillStyle = labelColor;
    // Screen name
    ctx.font = `bold ${nameFontSize}px sans-serif`;
    ctx.fillText(screenName, cx, cy - centerFontSize * 0.6);
    // Resolution
    ctx.font = `bold ${centerFontSize}px sans-serif`;
    ctx.fillText(resText, cx, cy + nameFontSize * 0.5);
    ctx.shadowBlur = 0;

    // Clear removed tiles (make transparent)
    const removedTiles = led.removedTiles || [];
    if (removedTiles.length > 0) {
      const removedSet = new Set(removedTiles);
      const isSoftCorner = ledMode === 'soft_corner';
      const leftCols = Math.max(1, led.leftColumns || Math.ceil(baseCols / 2));
      
      // Map grid column (from LedScreenProperties) to canvas column (from layout)
      const gridColToCanvasCol = (gc) => {
        if (isSoftCorner && gc >= leftCols) return gc + 1; // skip ribbon column
        return gc;
      };
      
      // Determine flat tile column count (from the grid UI)
      const isCorner = ledMode === 'corner';
      const rightCols = Math.max(1, led.rightColumns || Math.floor(baseCols / 2));
      const flatCols = (isCorner || isSoftCorner) ? leftCols + rightCols : baseCols;
      
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out'; // makes pixels transparent
      for (let r = 0; r < rows; r++) {
        for (let gc = 0; gc < flatCols; gc++) {
          const key = `${r}-${gc}`;
          if (removedSet.has(key)) {
            const cc = gridColToCanvasCol(gc);
            const x = colXScaled[cc];
            const cw = colWScaled[cc];
            const y = r * ch;
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.fillRect(x, y, cw, ch);
          }
        }
      }
      ctx.restore();
    }
  }, [cols, rows, totalResW, totalResH, tileResH, tileResV, pattern, primaryColor, secondaryColor, borderWidth, showGrid, showNumbers, showResolution, logoImage, activeLed, labelColor, labelSize, labelOffsetX, labelOffsetY, layout, led.removedTiles, led.leftColumns, led.rightColumns, ledMode, baseCols]);

  // Redraw preview on any change
  useEffect(() => {
    if (previewRef.current) {
      // Scale up so labels are crisp and visible in the small preview
      const previewScale = Math.max(1, Math.ceil(512 / Math.max(totalResW, totalResH)));
      drawCanvas(previewRef.current, previewScale);
    }
  }, [drawCanvas, totalResW, totalResH]);

  // ─── Logo upload ──────────────────────────────────────
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const img = new Image();
      img.onload = () => {
        setLogoImage(img);
        setLogoDataUrl(dataUrl);
        // Persist logo to worldSettings for save/load
        setWorldSettings(prev => ({ ...prev, testPatternLogo: dataUrl }));
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  // ─── Export full PNG ──────────────────────────────────
  const exportFullPng = () => {
    const offscreen = document.createElement('canvas');
    drawCanvas(offscreen, 1);
    offscreen.toBlob((blob) => {
      if (blob) { saveAs(blob, `LED_TestPattern_${pattern}_${totalResW}x${totalResH}.png`); toast.success('PNG exported'); }
    }, 'image/png');
  };

  // ─── Export All LED Panel Patterns (one PNG per LED wall, each with its own settings) ───
  const exportAllLedPanelPatterns = () => {
    if (ledScreens.length === 0) { toast.error('No LED screens in scene'); return; }
    let count = 0;
    ledScreens.forEach((screen, idx) => {
      const cfg = screen.ledConfig || {};
      const sMode = cfg.ledMode || 'standard';
      const sBaseCols = cfg.columns || 1;
      const sRows = cfg.rows || 1;
      const sTileW = cfg.tileWidth || 500;
      const sResH = cfg.tileResH || 128;
      const sResV = cfg.tileResV || 128;

      // Compute effective layout
      let sEffCols, sCornerPxH = 0;
      if (sMode === 'corner') {
        const lc = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const rc = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        sEffCols = lc + rc;
      } else if (sMode === 'soft_corner') {
        const lc = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const rc = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        sEffCols = lc + rc;
        sCornerPxH = Math.round(125 / sTileW * sResH);
      } else {
        sEffCols = sBaseCols;
        if (cfg.softCornerLeft) sCornerPxH += Math.round(125 / sTileW * sResH);
        if (cfg.softCornerRight) sCornerPxH += Math.round(125 / sTileW * sResH);
      }

      const w = sResH * sEffCols + sCornerPxH;
      const h = sResV * sRows;

      // Use this wall's own settings
      const ws = wallSettings[screen.id] || defaultSettings;

      const offscreen = document.createElement('canvas');
      offscreen.width = w;
      offscreen.height = h;
      const ctx = offscreen.getContext('2d');
      const cw = w / sEffCols, ch = h / sRows;

      // Draw this wall's selected pattern with its own colors
      switch (ws.pattern) {
        case 'solid': drawSolidColor(ctx, w, h, ws.primaryColor); break;
        case 'h_gradient': drawHorizontalGradient(ctx, w, h, ws.primaryColor, ws.secondaryColor); break;
        case 'v_gradient': drawVerticalGradient(ctx, w, h, ws.primaryColor, ws.secondaryColor); break;
        case 'checkerboard': drawCheckerboard(ctx, w, h, ws.primaryColor, ws.secondaryColor, sEffCols, sRows); break;
        case 'color_bars': drawColorBars(ctx, w, h); break;
        case 'grid_lines': drawGridLines(ctx, w, h, ws.primaryColor, sEffCols, sRows); break;
        case 'crosshatch': drawCrosshatch(ctx, w, h, ws.primaryColor); break;
        case 'focus': drawFocusPattern(ctx, w, h, ws.primaryColor); break;
        case 'rgb_test': drawRgbTest(ctx, w, h, sEffCols, sRows); break;
        case 'grayscale': drawGrayscale(ctx, w, h, sEffCols, sRows); break;
        case 'white': drawWhite(ctx, w, h); break;
        case 'black': drawBlack(ctx, w, h); break;
        default: drawSolidColor(ctx, w, h, ws.primaryColor);
      }

      // Overlays using this wall's settings
      if (ws.showGrid && ws.borderWidth > 0) {
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.lineWidth = ws.borderWidth;
        for (let c = 0; c <= sEffCols; c++) { ctx.beginPath(); ctx.moveTo(c * cw, 0); ctx.lineTo(c * cw, h); ctx.stroke(); }
        for (let r = 0; r <= sRows; r++) { ctx.beginPath(); ctx.moveTo(0, r * ch); ctx.lineTo(w, r * ch); ctx.stroke(); }
      }
      if (ws.showNumbers) {
        const fs = Math.max(12, Math.min(cw, ch) * 0.3);
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff'; ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 4;
        for (let r = 0; r < sRows; r++) for (let c = 0; c < sEffCols; c++) ctx.fillText(String(r * sEffCols + c + 1), c * cw + cw / 2, r * ch + ch / 2);
        ctx.shadowBlur = 0;
      }
      if (ws.showResolution) {
        const rfs = Math.max(8, Math.min(cw, ch) * 0.12);
        ctx.font = `${rfs}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillStyle = 'rgba(255,255,255,0.7)';
        for (let r = 0; r < sRows; r++) for (let c = 0; c < sEffCols; c++) ctx.fillText(`${sResH}x${sResV}`, c * cw + cw / 2, r * ch + 4);
      }

      // Logo overlay
      if (logoImage) {
        const lw = w * 0.3;
        const lh = (logoImage.height / logoImage.width) * lw;
        ctx.globalAlpha = 0.7;
        ctx.drawImage(logoImage, (w - lw) / 2, (h - lh) / 2, lw, lh);
        ctx.globalAlpha = 1;
      }

      // Center label: Screen name + Total resolution (per-wall settings)
      const sName = screen.name || `LED Screen ${idx + 1}`;
      const sResText = `${w} x ${h}`;
      const sf = (ws.labelSize || 50) / 50;
      const cfs = Math.max(14, Math.min(w, h) * 0.08 * sf);
      const nfs = Math.max(10, cfs * 0.65);
      const lcx = w / 2 + ((ws.labelOffsetX || 0) / 100) * w;
      const lcy = h / 2 + ((ws.labelOffsetY || 0) / 100) * h;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 5;
      ctx.fillStyle = ws.labelColor || '#ffffff';
      ctx.font = `bold ${nfs}px sans-serif`;
      ctx.fillText(sName, lcx, lcy - cfs * 0.6);
      ctx.font = `bold ${cfs}px sans-serif`;
      ctx.fillText(sResText, lcx, lcy + nfs * 0.5);
      ctx.shadowBlur = 0;

      const name = (screen.name || `LED_Screen_${idx + 1}`).replace(/\s+/g, '_');
      offscreen.toBlob((blob) => {
        if (blob) saveAs(blob, `${name}_${w}x${h}.png`);
        count++;
        if (count === ledScreens.length) toast.success(`${ledScreens.length} LED wall PNG(s) exported`);
      }, 'image/png');
    });
  };

  // ─── Helper: get or create "LED Patterns" folder ─────
  const ledPatternFolderIdRef = useRef(null);
  const getAuthHeaders = () => {
    const token = tokenStore.get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const getOrCreateLedFolder = async () => {
    if (ledPatternFolderIdRef.current) return ledPatternFolderIdRef.current;
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${API}/api/content/all`, { headers: getAuthHeaders() });
      const data = await res.json();
      const existing = data.folders?.find(f => f.name === 'LED Patterns');
      if (existing) { ledPatternFolderIdRef.current = existing.id; return existing.id; }
      // Create the folder
      const form = new FormData();
      form.append('name', 'LED Patterns');
      const createRes = await fetch(`${API}/api/content/folder`, { method: 'POST', body: form, headers: getAuthHeaders() });
      const folder = await createRes.json();
      ledPatternFolderIdRef.current = folder.id;
      return folder.id;
    } catch { return null; }
  };

  // ─── Apply to 3D scene ────────────────────────────────
  const applyToScene = async () => {
    if (!activeLed) { toast.error('No LED screen found in scene'); return; }
    // Render at higher resolution for crisp quality
    const exportScale = Math.max(1, Math.ceil(1024 / Math.max(totalResW, totalResH)));
    const offscreen = document.createElement('canvas');
    drawCanvas(offscreen, exportScale);

    // Flip horizontally to fix mirrored text on 3D plane back-face
    const flipped = document.createElement('canvas');
    flipped.width = offscreen.width;
    flipped.height = offscreen.height;
    const fCtx = flipped.getContext('2d');
    fCtx.translate(flipped.width, 0);
    fCtx.scale(-1, 1);
    fCtx.drawImage(offscreen, 0, 0);

    const folderId = await getOrCreateLedFolder();
    flipped.toBlob(async (blob) => {
      if (!blob) return;
      const formData = new FormData();
      formData.append('file', blob, `test_pattern_${activeLed.name || 'led'}.png`);
      if (folderId) formData.append('folder_id', folderId);
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/content/upload`, { method: 'POST', body: formData, headers: getAuthHeaders() });
        const data = await res.json();
        if (data.id) {
          const serveUrl = `/api/content/serve/${data.id}`;
          // Save contentUrl AND current test pattern settings together
          const currentSettings = wallSettings[activeLed.id] || defaultSettings;
          updateSceneObject(activeLed.id, {
            ledConfig: { ...activeLed.ledConfig, contentUrl: serveUrl, testPattern: currentSettings }
          });
          toast.success('Test pattern applied to LED wall');
        }
      } catch (err) {
        toast.error('Failed to apply pattern: ' + err.message);
      }
    }, 'image/png');
  };

  // ─── Export PDF report ────────────────────────────────
  const exportPdfReport = async () => {
    // Loading indicator — long exports (10+ walls) can take several seconds.
    // Using a manually-managed toast so we can update / dismiss it.
    const t = toast.loading('Generating LED report PDF — this can take a few seconds…');
    try {
      await _doExportPdfReport();
      toast.success('LED report PDF downloaded', { id: t });
    } catch (err) {
      console.error('LED PDF export failed:', err);
      toast.error('PDF export failed — please try again with fewer walls.', { id: t });
    }
  };

  const _doExportPdfReport = async () => {
    // `compress: true` runs zlib over text+vector content → ~30% smaller file.
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const pw = 210, margin = 15;
    const contentW = pw - margin * 2;
    let y = margin;

    // Toast + yield helper. Lets the browser breathe between walls so the
    // UI doesn't lock up when there are 5+ LED walls each spending tens of ms
    // on canvas → JPEG encoding. Yielding via rAF every wall keeps the page
    // responsive at the cost of a few extra ms total.
    const yieldToBrowser = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

    // Encodes a canvas as JPEG (much smaller than PNG) and embeds it. We
    // pre-paint a white backdrop on a clone so transparent regions
    // (e.g. removed tiles) come out white instead of black. The `'FAST'`
    // compression mode in jsPDF.addImage skips PNG re-encoding entirely.
    //
    // Optional `maxArea` shrinks oversized canvases (e.g. a 5120×2160 wall
    // pattern that's only displayed at 180×80 mm in the PDF doesn't need
    // 11 megapixels of detail). 1.4 megapixels is plenty for an A4 print.
    const MAX_IMG_AREA = 1_400_000;
    const addCanvasAsJpeg = (canvas, x, yPos, w, h, opts = {}) => {
      let src = canvas;
      // Downscale enormous canvases to keep file size + encoding time sane.
      if (canvas.width * canvas.height > MAX_IMG_AREA) {
        const scale = Math.sqrt(MAX_IMG_AREA / (canvas.width * canvas.height));
        const sw = Math.max(1, Math.round(canvas.width * scale));
        const sh = Math.max(1, Math.round(canvas.height * scale));
        const ds = document.createElement('canvas');
        ds.width = sw; ds.height = sh;
        ds.getContext('2d').drawImage(canvas, 0, 0, sw, sh);
        src = ds;
      }
      // White backdrop so JPEG flattening of transparent pixels is clean.
      const flat = document.createElement('canvas');
      flat.width = src.width; flat.height = src.height;
      const fctx = flat.getContext('2d');
      fctx.fillStyle = '#ffffff';
      fctx.fillRect(0, 0, flat.width, flat.height);
      fctx.drawImage(src, 0, 0);
      const data = flat.toDataURL('image/jpeg', opts.quality ?? 0.78);
      doc.addImage(data, 'JPEG', x, yPos, w, h, undefined, 'FAST');
    };

    // Title
    doc.setFontSize(20);
    doc.setTextColor(30, 30, 30);
    doc.text('LED Test Pattern Report', margin, y);
    y += 10;

    // Project info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Export Date: ${new Date().toLocaleString()}`, margin, y); y += 6;
    doc.text(`Total LED Screens: ${ledScreens.length}`, margin, y); y += 10;

    // Separator
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pw - margin, y); y += 8;

    // Track grand totals
    let grandTotalPower = 0;
    let grandTotalWeight = 0;
    let grandTotalPanels = 0;

    // ─── Capture top-view screenshot from 3D scene ─────
    let topViewImage = null;
    try {
      const scene = window.__babylonScene;
      const engine = window.__babylonEngine;
      const BABYLON = window.__BABYLON;
      if (scene && engine && BABYLON) {
        const camera = scene.activeCamera;
        // Save camera state
        const savedAlpha = camera.alpha, savedBeta = camera.beta;
        const savedRadius = camera.radius, savedMode = camera.mode;
        const savedOrthoT = camera.orthoTop, savedOrthoB = camera.orthoBottom;
        const savedOrthoL = camera.orthoLeft, savedOrthoR = camera.orthoRight;
        const savedTarget = camera.target.clone();
        // Set true top-down orthographic view, focused on the LED object
        const firstScreen = ledScreens[0];
        let ledMesh = null;
        if (firstScreen) {
          ledMesh = scene.getMeshByName('obj_' + firstScreen.id) || scene.getTransformNodeByName('obj_' + firstScreen.id);
        }
        if (ledMesh) {
          ledMesh.computeWorldMatrix(true);
          const bounds = ledMesh.getHierarchyBoundingVectors(true);
          const center = bounds.min.add(bounds.max).scale(0.5);
          camera.target = center;
        }
        camera.alpha = -Math.PI / 2;
        camera.beta = 0.01; // true top-down
        camera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
        const aspect = engine.getAspectRatio(camera) || 1.78;
        // Zoom to fit the LED object tightly
        let orthoSize = camera.radius || 15;
        if (ledMesh) {
          const bounds = ledMesh.getHierarchyBoundingVectors(true);
          const extents = bounds.max.subtract(bounds.min);
          // Tighter framing (15% padding instead of 30%) — the wall fills
          // more of the snapshot so its proportions are readable when the
          // image is embedded in the PDF.
          orthoSize = Math.max(extents.x, extents.z) * 1.15;
        }
        camera.orthoTop = orthoSize / 2;
        camera.orthoBottom = -orthoSize / 2;
        camera.orthoLeft = -orthoSize * aspect / 2;
        camera.orthoRight = orthoSize * aspect / 2;
        // Higher-resolution capture (1600×900) so when we enlarge the embed
        // in the PDF (now ~120 mm wide) the image still looks crisp.
        topViewImage = await new Promise((resolve) => {
          BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, camera, { width: 1600, height: 900 }, (data) => {
            resolve(data);
          });
        });
        // Restore camera
        camera.alpha = savedAlpha; camera.beta = savedBeta;
        camera.radius = savedRadius; camera.mode = savedMode;
        camera.orthoTop = savedOrthoT; camera.orthoBottom = savedOrthoB;
        camera.orthoLeft = savedOrthoL; camera.orthoRight = savedOrthoR;
        camera.target = savedTarget;
        scene.render();
      }
    } catch (e) { console.warn('Top view capture failed:', e); }

    // ─── Per-LED-wall details ─────────────────────────
    // Async for-of so we can yield to the browser between walls — encoding
    // each wall's pattern → JPEG can take 50–200 ms; yielding keeps the UI
    // responsive (no "page unresponsive" prompts when there are 6+ walls).
    for (let idx = 0; idx < ledScreens.length; idx += 1) {
      const screen = ledScreens[idx];
      const cfg = screen.ledConfig || {};
      const sMode = cfg.ledMode || 'standard';
      const sBaseCols = cfg.columns || 1;
      const sRows = cfg.rows || 1;
      const sTileW = cfg.tileWidth || 500;
      const sTileH = cfg.tileHeight || 500;
      const sResH = cfg.tileResH || 128;
      const sResV = cfg.tileResV || 128;

      // Compute effective layout per mode
      let sEffCols, sTotalPhysW, sCornerPxH = 0;
      if (sMode === 'corner') {
        const lc = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const rc = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        sEffCols = lc + rc;
        sTotalPhysW = sEffCols * sTileW;
      } else if (sMode === 'soft_corner') {
        const lc = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const rc = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        sEffCols = lc + rc;
        sTotalPhysW = lc * sTileW + 125 + rc * sTileW;
        sCornerPxH = Math.round(125 / sTileW * sResH);
      } else {
        sEffCols = sBaseCols;
        sTotalPhysW = sTileW * sBaseCols;
        if (cfg.softCornerLeft) { sTotalPhysW += 125; sCornerPxH += Math.round(125 / sTileW * sResH); }
        if (cfg.softCornerRight) { sTotalPhysW += 125; sCornerPxH += Math.round(125 / sTileW * sResH); }
      }

      const sPanels = sEffCols * sRows;
      const sRemovedCount = (cfg.removedTiles || []).length;
      const sActivePanels = sPanels - sRemovedCount;
      const sPhysW = sTotalPhysW / 1000;
      const sPhysH = (sTileH * sRows) / 1000;
      const sTotalResW = sResH * sEffCols + sCornerPxH;
      const sTotalResH = sResV * sRows;
      const sPower = (cfg.powerPerTile || 200) * sActivePanels;
      const sWeight = (cfg.weightPerTile || 12) * sActivePanels;

      grandTotalPower += sPower;
      grandTotalWeight += sWeight;
      grandTotalPanels += sActivePanels;

      // Check page space
      if (y > 240) { doc.addPage(); y = margin; }

      // Wall title
      doc.setFontSize(14);
      doc.setTextColor(30, 30, 30);
      doc.text(`${idx + 1}. ${screen.name || 'LED Screen'}`, margin, y); y += 8;

      // Specs
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      const specs = [
        ['Screen Size', `${sPhysW.toFixed(2)}m x ${sPhysH.toFixed(2)}m`],
        ['Panels', `${sEffCols} x ${sRows} (${sActivePanels} active${sRemovedCount > 0 ? `, ${sRemovedCount} removed` : ''})`],
        ['Panel Size', `${sTileW}mm x ${sTileH}mm`],
        ['Panel Resolution', `${sResH} x ${sResV} px`],
        ['Total Resolution', `${sTotalResW} x ${sTotalResH} px`],
        ['LED Mode', sMode],
      ];
      // Add curve info for curved/flexible LEDs
      if (sMode === 'curved' || sMode === 'flexible') {
        const curveDir = cfg.curveDirection || 'convex';
        const curveAngle = cfg.curveAngle || 30;
        const curveRadius = cfg.curveRadius || 5;
        specs.push(['Curve Direction', curveDir.charAt(0).toUpperCase() + curveDir.slice(1)]);
        specs.push(['Curve Angle', `${curveAngle}°`]);
        specs.push(['Curve Radius', `${curveRadius.toFixed ? curveRadius.toFixed(2) : curveRadius}m`]);
      }
      // Add per-panel breakdown for corner/soft corner
      if (sMode === 'corner' || sMode === 'soft_corner') {
        const sLeftCols = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const sRightCols = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        const leftTiles = sLeftCols * sRows;
        const rightTiles = sRightCols * sRows;
        const leftRemoved = (cfg.removedTiles || []).filter(k => { const c = parseInt(k.split('-')[1]); return c < sLeftCols; }).length;
        const rightRemoved = (cfg.removedTiles || []).filter(k => { const c = parseInt(k.split('-')[1]); return c >= sLeftCols; }).length;
        specs.push(['Left Wall', `${sLeftCols} x ${sRows} = ${leftTiles - leftRemoved} tiles (${(sLeftCols * sTileW / 1000).toFixed(2)}m)${leftRemoved > 0 ? ` [${leftRemoved} removed]` : ''}`]);
        if (sMode === 'soft_corner') {
          specs.push(['Center Curve', `Ribbon — ${cfg.cornerAngle || 90}°`]);
        }
        specs.push(['Right Wall', `${sRightCols} x ${sRows} = ${rightTiles - rightRemoved} tiles (${(sRightCols * sTileW / 1000).toFixed(2)}m)${rightRemoved > 0 ? ` [${rightRemoved} removed]` : ''}`]);
        specs.push(['Normal Tiles', `${sActivePanels}`]);
        if (sMode === 'soft_corner') specs.push(['Corner Tiles', `1 curved ribbon section`]);
      }
      // Standard LED with soft corners — add detailed corner info
      if ((sMode === 'standard' || sMode === 'curved' || sMode === 'flexible') && (cfg.softCornerLeft || cfg.softCornerRight)) {
        const scTileW = cfg.tileWidth || 500;
        const scTileH = cfg.tileHeight || 500;
        const scModuleW = 125; // soft corner module width mm
        const scTilesPerCorner = Math.round(scTileH / scModuleW); // typically 4 modules per corner
        specs.push(['90° Soft Corner', `${scModuleW}mm x ${scTileH}mm modules`]);
        if (cfg.softCornerLeft) {
          specs.push(['Left Corner', `Enabled — ${scTilesPerCorner} modules x ${sRows} rows = ${scTilesPerCorner * sRows} corner tiles`]);
        }
        if (cfg.softCornerRight) {
          specs.push(['Right Corner', `Enabled — ${scTilesPerCorner} modules x ${sRows} rows = ${scTilesPerCorner * sRows} corner tiles`]);
        }
        const totalCornerTiles = ((cfg.softCornerLeft ? 1 : 0) + (cfg.softCornerRight ? 1 : 0)) * scTilesPerCorner * sRows;
        specs.push(['Total with Corners', `${sActivePanels} flat + ${totalCornerTiles} corner = ${sActivePanels + totalCornerTiles} total tiles`]);
      }
      specs.forEach(([k, v]) => {
        doc.setFont(undefined, 'bold'); doc.text(k + ':', margin + 4, y);
        doc.setFont(undefined, 'normal'); doc.text(v, margin + 48, y);
        y += 4;
      });
      y += 2;

      // Power with breakdown
      doc.setFont(undefined, 'bold'); doc.text('Power:', margin + 4, y);
      doc.setFont(undefined, 'normal'); doc.text(`${sPower}W (${(sPower / 1000).toFixed(1)}kW) — ${(sPower / 230).toFixed(1)}A @ 230V`, margin + 48, y);
      y += 4;
      if (sMode === 'corner' || sMode === 'soft_corner') {
        const pLeftCols = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const pRightCols = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        const powerPerTile = cfg.powerPerTile || 200;
        const leftActive3 = pLeftCols * sRows - (cfg.removedTiles || []).filter(k => parseInt(k.split('-')[1]) < pLeftCols).length;
        const rightActive3 = pRightCols * sRows - (cfg.removedTiles || []).filter(k => parseInt(k.split('-')[1]) >= pLeftCols).length;
        doc.setFont(undefined, 'normal');
        doc.text(`  Left: ${leftActive3 * powerPerTile}W (${leftActive3} panels) — Right: ${rightActive3 * powerPerTile}W (${rightActive3} panels)`, margin + 4, y);
        y += 4;
      }
      y += 4;

      // Weight with breakdown
      doc.setFont(undefined, 'bold'); doc.text('Weight:', margin + 4, y);
      const weightPerPanel = cfg.weightPerTile || 12;
      doc.setFont(undefined, 'normal'); doc.text(`${sWeight} kg (${weightPerPanel} kg/panel x ${sActivePanels} active panels)`, margin + 48, y);
      y += 4;
      if (sMode === 'corner' || sMode === 'soft_corner') {
        const wLeftCols = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const wRightCols = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        const leftActive = wLeftCols * sRows - (cfg.removedTiles || []).filter(k => parseInt(k.split('-')[1]) < wLeftCols).length;
        const rightActive = wRightCols * sRows - (cfg.removedTiles || []).filter(k => parseInt(k.split('-')[1]) >= wLeftCols).length;
        doc.setFont(undefined, 'normal');
        doc.text(`  Left Wall: ${leftActive * weightPerPanel} kg (${leftActive} panels) — Right Wall: ${rightActive * weightPerPanel} kg (${rightActive} panels)`, margin + 4, y);
        y += 4;
      }
      y += 4;

      // Pixel map image for this wall using its own settings
      const ws = wallSettings[screen.id] || defaultSettings;
      const sCw = sTotalResW, sCh = sTotalResH;
      const cellW = sCw / sEffCols, cellH = sCh / sRows;

      // Helper to draw a single pattern on an offscreen canvas
      const drawPatternCanvas = (pid) => {
        const offscreen = document.createElement('canvas');
        offscreen.width = sCw; offscreen.height = sCh;
        const ctx = offscreen.getContext('2d');
        switch (pid) {
          case 'solid': drawSolidColor(ctx, sCw, sCh, ws.primaryColor); break;
          case 'h_gradient': drawHorizontalGradient(ctx, sCw, sCh, ws.primaryColor, ws.secondaryColor); break;
          case 'v_gradient': drawVerticalGradient(ctx, sCw, sCh, ws.primaryColor, ws.secondaryColor); break;
          case 'checkerboard': drawCheckerboard(ctx, sCw, sCh, ws.primaryColor, ws.secondaryColor, sEffCols, sRows); break;
          case 'color_bars': drawColorBars(ctx, sCw, sCh); break;
          case 'grid_lines': drawGridLines(ctx, sCw, sCh, ws.primaryColor, sEffCols, sRows); break;
          case 'crosshatch': drawCrosshatch(ctx, sCw, sCh, ws.primaryColor); break;
          case 'focus': drawFocusPattern(ctx, sCw, sCh, ws.primaryColor); break;
          case 'rgb_test': drawRgbTest(ctx, sCw, sCh, sEffCols, sRows); break;
          case 'grayscale': drawGrayscale(ctx, sCw, sCh, sEffCols, sRows); break;
          case 'white': drawWhite(ctx, sCw, sCh); break;
          case 'black': drawBlack(ctx, sCw, sCh); break;
          default: drawSolidColor(ctx, sCw, sCh, ws.primaryColor);
        }
        // Logo overlay
        if (logoImage) {
          const lw = sCw * 0.3;
          const lh = (logoImage.height / logoImage.width) * lw;
          ctx.globalAlpha = 0.7;
          ctx.drawImage(logoImage, (sCw - lw) / 2, (sCh - lh) / 2, lw, lh);
          ctx.globalAlpha = 1;
        }
        // Grid
        if (ws.showGrid && ws.borderWidth > 0) {
          ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = ws.borderWidth;
          for (let c = 0; c <= sEffCols; c++) { ctx.beginPath(); ctx.moveTo(c * cellW, 0); ctx.lineTo(c * cellW, sCh); ctx.stroke(); }
          for (let r = 0; r <= sRows; r++) { ctx.beginPath(); ctx.moveTo(0, r * cellH); ctx.lineTo(sCw, r * cellH); ctx.stroke(); }
        }
        // Numbers
        if (ws.showNumbers) {
          const fs = Math.max(10, Math.min(cellW, cellH) * 0.3);
          ctx.font = `bold ${fs}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillStyle = '#ffffff'; ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 3;
          for (let r = 0; r < sRows; r++) for (let c = 0; c < sEffCols; c++) ctx.fillText(String(r * sEffCols + c + 1), c * cellW + cellW / 2, r * cellH + cellH / 2);
          ctx.shadowBlur = 0;
        }
        // Center label: Screen name + Total resolution (per-wall settings)
        const sName = screen.name || 'LED Screen';
        const sResText = `${sTotalResW} x ${sTotalResH}`;
        const sf = (ws.labelSize || 50) / 50;
        const cfs = Math.max(14, Math.min(sCw, sCh) * 0.08 * sf);
        const nfs = Math.max(10, cfs * 0.65);
        const lcx = sCw / 2 + ((ws.labelOffsetX || 0) / 100) * sCw;
        const lcy = sCh / 2 + ((ws.labelOffsetY || 0) / 100) * sCh;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 5;
        ctx.fillStyle = ws.labelColor || '#ffffff';
        ctx.font = `bold ${nfs}px sans-serif`;
        ctx.fillText(sName, lcx, lcy - cfs * 0.6);
        ctx.font = `bold ${cfs}px sans-serif`;
        ctx.fillText(sResText, lcx, lcy + nfs * 0.5);
        ctx.shadowBlur = 0;

        // Clear removed tiles (transparent in export)
        const removedTiles = screen.ledConfig?.removedTiles || [];
        if (removedTiles.length > 0) {
          const removedSet = new Set(removedTiles);
          const sLedMode = screen.ledConfig?.ledMode || 'standard';
          const sIsCorner = sLedMode === 'corner';
          const sIsSoftCorner = sLedMode === 'soft_corner';
          const sBaseCols = screen.ledConfig?.columns || 4;
          const sLeftCols = Math.max(1, screen.ledConfig?.leftColumns || Math.ceil(sBaseCols / 2));
          const sRightCols = Math.max(1, screen.ledConfig?.rightColumns || Math.floor(sBaseCols / 2));
          const sFlatCols = (sIsCorner || sIsSoftCorner) ? sLeftCols + sRightCols : sBaseCols;
          
          ctx.save();
          ctx.globalCompositeOperation = 'destination-out';
          const sCellH = sCh / sRows;
          const sCellW = sCw / sFlatCols;
          for (let r = 0; r < sRows; r++) {
            for (let gc = 0; gc < sFlatCols; gc++) {
              if (removedSet.has(`${r}-${gc}`)) {
                ctx.fillStyle = 'rgba(0,0,0,1)';
                ctx.fillRect(gc * sCellW, r * sCellH, sCellW, sCellH);
              }
            }
          }
          ctx.restore();
        }
        return offscreen;
      };

      // Render the SELECTED pattern for this wall (with logo)
      const offscreen = drawPatternCanvas(ws.pattern);
      const imgAspect = sCw / sCh;
      const imgW = contentW;
      // Allow the embedded pattern preview to grow taller (was capped at 50mm
      // which made very square / portrait walls look postage-stamp small).
      const imgH = Math.min(imgW / imgAspect, 90);
      const actualW = imgH * imgAspect;
      if (y + imgH + 8 > 285) { doc.addPage(); y = margin; }
      const patLabel = PATTERNS.find(p => p.id === ws.pattern)?.label || ws.pattern;
      doc.setFontSize(10); doc.setTextColor(50, 50, 50);
      doc.text(`Pattern: ${patLabel}`, margin + 4, y); y += 5;
      addCanvasAsJpeg(offscreen, margin, y, actualW, imgH);
      y += imgH + 6;

      // Corner/Soft Corner: add per-wall patterns + views
      if (sMode === 'corner' || sMode === 'soft_corner') {
        const cLeftCols = Math.max(1, cfg.leftColumns || Math.ceil(sBaseCols / 2));
        const cRightCols = Math.max(1, cfg.rightColumns || Math.floor(sBaseCols / 2));
        const cAngle = cfg.cornerAngle || 90;
        
        // Left Wall pattern
        doc.setFontSize(8); doc.setTextColor(50, 50, 50);
        doc.text(`Left Wall Pattern (${cLeftCols} x ${sRows} tiles)`, margin + 4, y); y += 5;
        const leftCanvas = document.createElement('canvas');
        const leftResW = sResH * cLeftCols, leftResH = sResV * sRows;
        leftCanvas.width = leftResW; leftCanvas.height = leftResH;
        const lCtx = leftCanvas.getContext('2d');
        lCtx.drawImage(offscreen, 0, 0, sResH * cLeftCols, sCh, 0, 0, leftResW, leftResH);
        const lAspect = leftResW / leftResH;
        const lH = Math.min(30, contentW / 2 / lAspect);
        const lW = lH * lAspect;
        addCanvasAsJpeg(leftCanvas, margin, y, lW, lH);
        
        // Right Wall pattern (next to left)
        doc.text(`Right Wall Pattern (${cRightCols} x ${sRows} tiles)`, margin + lW + 10, y - 5);
        const rightCanvas = document.createElement('canvas');
        const rightResW = sResH * cRightCols, rightResH = sResV * sRows;
        rightCanvas.width = rightResW; rightCanvas.height = rightResH;
        const rCtx = rightCanvas.getContext('2d');
        rCtx.drawImage(offscreen, sResH * cLeftCols, 0, sResH * cRightCols, sCh, 0, 0, rightResW, rightResH);
        const rAspect = rightResW / rightResH;
        const rH = Math.min(30, (contentW - lW - 10) / rAspect);
        const rW = rH * rAspect;
        addCanvasAsJpeg(rightCanvas, margin + lW + 10, y, rW, rH);
        y += Math.max(lH, rH) + 4;

        // Top View diagram (bird's eye)
        doc.setFontSize(8); doc.text('Top View', margin + 4, y); y += 4;
        const topCanvas = document.createElement('canvas');
        topCanvas.width = 400; topCanvas.height = 250;
        const tc = topCanvas.getContext('2d');
        tc.fillStyle = '#f8f8f8'; tc.fillRect(0, 0, 400, 250);
        tc.strokeStyle = '#333'; tc.lineWidth = 2;
        // Corner angle = angle between walls. Opening = 180 - corner angle
        const cx = 200, cy = 40;
        const leftArmLen = 80 * (cLeftCols / Math.max(cLeftCols, cRightCols));
        const rightArmLen = 80 * (cRightCols / Math.max(cLeftCols, cRightCols));
        // Half of corner angle for each side (walls go DOWN from the corner vertex)
        const halfAngle = (cAngle / 2) * Math.PI / 180;
        const lx = cx - Math.sin(halfAngle) * (leftArmLen + 40);
        const ly = cy + Math.cos(halfAngle) * (leftArmLen + 40);
        const rx = cx + Math.sin(halfAngle) * (rightArmLen + 40);
        const ry = cy + Math.cos(halfAngle) * (rightArmLen + 40);
        // Draw thick wall lines
        tc.lineWidth = 8; tc.strokeStyle = '#2563eb';
        tc.beginPath(); tc.moveTo(cx, cy); tc.lineTo(lx, ly); tc.stroke();
        tc.strokeStyle = '#d97706';
        tc.beginPath(); tc.moveTo(cx, cy); tc.lineTo(rx, ry); tc.stroke();
        // Corner dot
        tc.fillStyle = '#333'; tc.beginPath(); tc.arc(cx, cy, 5, 0, Math.PI * 2); tc.fill();
        // Angle arc
        tc.strokeStyle = '#dc2626'; tc.lineWidth = 1.5;
        const arcR = 30;
        tc.beginPath(); tc.arc(cx, cy, arcR, Math.PI/2 - halfAngle, Math.PI/2 + halfAngle); tc.stroke();
        // Labels
        tc.fillStyle = '#dc2626'; tc.font = 'bold 16px sans-serif'; tc.textAlign = 'center';
        tc.fillText(`${cAngle}°`, cx, cy + arcR + 18);
        tc.fillStyle = '#2563eb'; tc.font = 'bold 13px sans-serif';
        tc.fillText(`Left: ${cLeftCols} tiles`, lx - 30, ly + 20);
        tc.fillStyle = '#d97706';
        tc.fillText(`Right: ${cRightCols} tiles`, rx + 30, ry + 20);
        // Viewer arrow
        tc.fillStyle = '#666'; tc.font = '12px sans-serif';
        tc.fillText('▼ Viewer', cx, cy + Math.max(leftArmLen, rightArmLen) + 80);
        addCanvasAsJpeg(topCanvas, margin, y, contentW * 0.45, contentW * 0.22);
        
        // Front View diagram (next to top view)
        doc.text('Front View', margin + contentW * 0.5, y - 4);
        const frontCanvas = document.createElement('canvas');
        frontCanvas.width = 400; frontCanvas.height = 250;
        const fc = frontCanvas.getContext('2d');
        fc.fillStyle = '#f8f8f8'; fc.fillRect(0, 0, 400, 250);
        fc.strokeStyle = '#333'; fc.lineWidth = 2;
        // Left panel rect
        const fLeftW = (cLeftCols / (cLeftCols + cRightCols)) * 350;
        const fRightW = (cRightCols / (cLeftCols + cRightCols)) * 350;
        const fH = 140;
        const fStartX = 25;
        fc.fillStyle = '#dbeafe'; fc.fillRect(fStartX, 30, fLeftW, fH);
        fc.strokeRect(fStartX, 30, fLeftW, fH);
        // Grid lines for left
        for (let i = 1; i < cLeftCols; i++) { fc.beginPath(); fc.moveTo(fStartX + i * fLeftW / cLeftCols, 30); fc.lineTo(fStartX + i * fLeftW / cLeftCols, 30 + fH); fc.stroke(); }
        for (let i = 1; i < sRows; i++) { fc.beginPath(); fc.moveTo(fStartX, 30 + i * fH / sRows); fc.lineTo(fStartX + fLeftW, 30 + i * fH / sRows); fc.stroke(); }
        // Right panel rect
        fc.fillStyle = '#fef3c7'; fc.fillRect(fStartX + fLeftW + 5, 30, fRightW, fH);
        fc.strokeRect(fStartX + fLeftW + 5, 30, fRightW, fH);
        for (let i = 1; i < cRightCols; i++) { fc.beginPath(); fc.moveTo(fStartX + fLeftW + 5 + i * fRightW / cRightCols, 30); fc.lineTo(fStartX + fLeftW + 5 + i * fRightW / cRightCols, 30 + fH); fc.stroke(); }
        for (let i = 1; i < sRows; i++) { fc.beginPath(); fc.moveTo(fStartX + fLeftW + 5, 30 + i * fH / sRows); fc.lineTo(fStartX + fLeftW + 5 + fRightW, 30 + i * fH / sRows); fc.stroke(); }
        // Labels
        fc.fillStyle = '#2563eb'; fc.font = 'bold 13px sans-serif'; fc.textAlign = 'center';
        fc.fillText(`Left Wall`, fStartX + fLeftW / 2, 20);
        fc.fillText(`${cLeftCols}x${sRows}`, fStartX + fLeftW / 2, 30 + fH + 15);
        fc.fillStyle = '#d97706';
        fc.fillText(`Right Wall`, fStartX + fLeftW + 5 + fRightW / 2, 20);
        fc.fillText(`${cRightCols}x${sRows}`, fStartX + fLeftW + 5 + fRightW / 2, 30 + fH + 15);
        addCanvasAsJpeg(frontCanvas, margin + contentW * 0.5, y, contentW * 0.5, contentW * 0.22);
        y += contentW * 0.22 + 4;
      }

      // Curved/Flexible LED: add top view diagram showing curve
      if (sMode === 'curved' || sMode === 'flexible') {
        const cvDir = cfg.curveDirection || 'convex';
        const cvAngle = cfg.curveAngle || 30;
        const cvRadius = cfg.curveRadius || 5;
        doc.setFontSize(8); doc.setTextColor(50, 50, 50);
        doc.text(`Top View — ${cvDir.charAt(0).toUpperCase() + cvDir.slice(1)} Curve ${cvAngle}°, R=${typeof cvRadius === 'number' ? cvRadius.toFixed(2) : cvRadius}m`, margin + 4, y); y += 3;
        const cvCanvas = document.createElement('canvas');
        cvCanvas.width = 400; cvCanvas.height = 150;
        const cv = cvCanvas.getContext('2d');
        cv.fillStyle = '#f8f8f8'; cv.fillRect(0, 0, 400, 150);
        // Draw arc from top view
        const arcCx = 200, arcCy = cvDir === 'convex' ? 130 : 20;
        const drawR = 80;
        const halfArc = (cvAngle / 2) * Math.PI / 180;
        const startA = cvDir === 'convex' ? -Math.PI / 2 - halfArc : Math.PI / 2 - halfArc;
        const endA = cvDir === 'convex' ? -Math.PI / 2 + halfArc : Math.PI / 2 + halfArc;
        // Arc
        cv.strokeStyle = '#2563eb'; cv.lineWidth = 6;
        cv.beginPath(); cv.arc(arcCx, arcCy, drawR, startA, endA); cv.stroke();
        // End markers
        const sx = arcCx + drawR * Math.cos(startA), sy = arcCy + drawR * Math.sin(startA);
        const ex = arcCx + drawR * Math.cos(endA), ey = arcCy + drawR * Math.sin(endA);
        cv.fillStyle = '#dc2626'; cv.beginPath(); cv.arc(sx, sy, 4, 0, Math.PI * 2); cv.fill();
        cv.beginPath(); cv.arc(ex, ey, 4, 0, Math.PI * 2); cv.fill();
        // Radius line
        cv.strokeStyle = '#999'; cv.lineWidth = 1; cv.setLineDash([4, 3]);
        const midA = (startA + endA) / 2;
        const mx = arcCx + drawR * Math.cos(midA), my = arcCy + drawR * Math.sin(midA);
        cv.beginPath(); cv.moveTo(arcCx, arcCy); cv.lineTo(mx, my); cv.stroke();
        cv.setLineDash([]);
        // Labels
        cv.fillStyle = '#333'; cv.font = 'bold 14px sans-serif'; cv.textAlign = 'center';
        cv.fillText(`${cvAngle}°`, arcCx, cvDir === 'convex' ? 30 : 130);
        cv.fillStyle = '#666'; cv.font = '11px sans-serif';
        cv.fillText(`R=${typeof cvRadius === 'number' ? cvRadius.toFixed(2) : cvRadius}m`, (arcCx + mx) / 2 + 20, (arcCy + my) / 2);
        cv.fillText(`${sBaseCols} panels`, arcCx, cvDir === 'convex' ? 50 : 110);
        cv.fillText('▼ Viewer', arcCx, cvDir === 'convex' ? 15 : 145);
        const cvAspect = 400 / 150;
        const cvH = Math.min(28, contentW * 0.6 / cvAspect);
        addCanvasAsJpeg(cvCanvas, margin, y, cvH * cvAspect, cvH);
        // Add 3D top view screenshot next to diagram — bigger now so the
        // wall geometry is actually readable.
        if (topViewImage) {
          const tvW = contentW - cvH * cvAspect - 8;
          const tvH = Math.max(cvH, tvW / (16 / 9));
          doc.addImage(topViewImage, 'JPEG', margin + cvH * cvAspect + 8, y, tvW, tvH, undefined, 'FAST');
          if (tvH > cvH) y += (tvH - cvH); // push the cursor down to clear the taller image
        }
        y += cvH + 4;
      }

      // For non-curved LEDs (corner, soft_corner, standard): add a LARGE
      // 3D top-view screenshot. Previously this was clipped to ~28 mm tall
      // which made the wall hard to read. New cap is ~90 mm tall (≈ half a
      // portrait A4 page width × 1.78 = 160 mm wide) so the image is now
      // ~4× larger by area while still leaving room for the front-view
      // diagram further down the page.
      if (sMode !== 'curved' && sMode !== 'flexible' && topViewImage) {
        doc.setFontSize(8); doc.text('3D Top View', margin + 4, y); y += 3;
        const tvAspect = 16 / 9;
        const tvW = contentW;
        const tvH = tvW / tvAspect;
        if (y + tvH + 8 > 285) { doc.addPage(); y = margin; }
        doc.addImage(topViewImage, 'JPEG', margin, y, tvW, tvH, undefined, 'FAST');
        y += tvH + 4;
      }

      // Standard LED with soft corners: add front view diagram
      if ((sMode === 'standard' || sMode === 'curved' || sMode === 'flexible') && (cfg.softCornerLeft || cfg.softCornerRight)) {
        doc.setFontSize(8); doc.setTextColor(50, 50, 50);
        doc.text('Front View (with Soft Corners)', margin + 4, y); y += 4;
        const scCanvas = document.createElement('canvas');
        scCanvas.width = 500; scCanvas.height = 180;
        const sc = scCanvas.getContext('2d');
        sc.fillStyle = '#f8f8f8'; sc.fillRect(0, 0, 500, 180);
        
        const totalParts = (cfg.softCornerLeft ? 1 : 0) + sBaseCols + (cfg.softCornerRight ? 1 : 0);
        const partW = 400 / totalParts;
        const fH = 120;
        let xPos = 50;
        
        // Left corner
        if (cfg.softCornerLeft) {
          sc.fillStyle = '#fee2e2'; sc.fillRect(xPos, 25, partW, fH);
          sc.strokeStyle = '#dc2626'; sc.lineWidth = 2; sc.strokeRect(xPos, 25, partW, fH);
          // Draw curve indicator
          sc.beginPath(); sc.arc(xPos + partW, 25 + fH / 2, partW * 0.8, Math.PI * 0.6, Math.PI * 1.4); sc.stroke();
          sc.fillStyle = '#dc2626'; sc.font = 'bold 11px sans-serif'; sc.textAlign = 'center';
          sc.fillText('L Corner', xPos + partW / 2, 18);
          sc.fillText('90°', xPos + partW / 2, 25 + fH + 15);
          xPos += partW;
        }
        
        // Main flat panels
        sc.fillStyle = '#dbeafe'; sc.fillRect(xPos, 25, partW * sBaseCols, fH);
        sc.strokeStyle = '#2563eb'; sc.lineWidth = 2; sc.strokeRect(xPos, 25, partW * sBaseCols, fH);
        // Grid
        sc.strokeStyle = '#93c5fd'; sc.lineWidth = 0.5;
        for (let i = 1; i < sBaseCols; i++) { sc.beginPath(); sc.moveTo(xPos + i * partW, 25); sc.lineTo(xPos + i * partW, 25 + fH); sc.stroke(); }
        for (let i = 1; i < sRows; i++) { sc.beginPath(); sc.moveTo(xPos, 25 + i * fH / sRows); sc.lineTo(xPos + partW * sBaseCols, 25 + i * fH / sRows); sc.stroke(); }
        sc.fillStyle = '#2563eb'; sc.font = 'bold 12px sans-serif'; sc.textAlign = 'center';
        sc.fillText(`Main: ${sBaseCols} x ${sRows} tiles`, xPos + partW * sBaseCols / 2, 18);
        sc.fillText(`${sActivePanels} panels`, xPos + partW * sBaseCols / 2, 25 + fH + 15);
        xPos += partW * sBaseCols;
        
        // Right corner
        if (cfg.softCornerRight) {
          sc.fillStyle = '#fef3c7'; sc.fillRect(xPos, 25, partW, fH);
          sc.strokeStyle = '#d97706'; sc.lineWidth = 2; sc.strokeRect(xPos, 25, partW, fH);
          sc.beginPath(); sc.arc(xPos, 25 + fH / 2, partW * 0.8, -Math.PI * 0.4, Math.PI * 0.4); sc.stroke();
          sc.fillStyle = '#d97706'; sc.font = 'bold 11px sans-serif'; sc.textAlign = 'center';
          sc.fillText('R Corner', xPos + partW / 2, 18);
          sc.fillText('90°', xPos + partW / 2, 25 + fH + 15);
        }
        
        const scAspect = 500 / 180;
        const scImgH = Math.min(30, contentW / scAspect);
        addCanvasAsJpeg(scCanvas, margin, y, scImgH * scAspect, scImgH);
        y += scImgH + 4;
      }

      // Separator between walls
      if (idx < ledScreens.length - 1) {
        doc.setDrawColor(220, 220, 220);
        doc.line(margin, y, pw - margin, y);
        y += 6;
      }
      // Let the browser breathe — repaints, processes input — before the
      // next wall's heavy canvas + JPEG encoding pass.
      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
    }

    // ─── Grand Totals ─────────────────────────────────
    if (y > 230) { doc.addPage(); y = margin; }
    doc.setDrawColor(100, 100, 100);
    doc.line(margin, y, pw - margin, y); y += 8;

    doc.setFontSize(16);
    doc.setTextColor(30, 30, 30);
    doc.text('Total Summary (All LED Walls)', margin, y); y += 10;

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const totals = [
      ['Total LED Screens', `${ledScreens.length}`],
      ['Total Panels', `${grandTotalPanels}`],
      ['Total Power Load', `${grandTotalPower}W (${(grandTotalPower / 1000).toFixed(1)}kW)`],
      ['Total Current @ 230V', `${(grandTotalPower / 230).toFixed(2)} Amps`],
      ['Total Weight', `${grandTotalWeight} kg`],
    ];
    totals.forEach(([k, v]) => {
      doc.setFont(undefined, 'bold'); doc.text(k + ':', margin, y);
      doc.setFont(undefined, 'normal'); doc.text(v, margin + 55, y);
      y += 7;
    });

    doc.save(`LED_Report_${ledScreens.length}_walls.pdf`);
    toast.success('PDF report exported');
  };

  // ─── Render ───────────────────────────────────────────
  const previewMaxW = 600;
  const aspect = totalResW / totalResH;
  const previewW = Math.min(previewMaxW, totalResW);
  const previewH = previewW / aspect;

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="led-test-pattern">
      {/* If no LED screen detected */}
      {ledScreens.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-zinc-500 space-y-2">
            <Monitor className="w-8 h-8 mx-auto text-zinc-600" />
            <p className="text-sm">No LED screen in scene</p>
            <p className="text-xs">Add a Standard LED Wall from the Primitives tab to use the test pattern generator.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="led-test-pattern-content">
          {/* LED Wall Selector */}
          {ledScreens.length > 1 && (
            <div className="space-y-1.5" data-testid="led-wall-selector">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Select LED Wall</div>
              <div className="space-y-1">
                {ledScreens.map((screen, idx) => (
                  <button
                    key={screen.id}
                    onClick={() => setActiveLedId(screen.id)}
                    className={`w-full text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                      activeLed?.id === screen.id
                        ? 'border-teal-500 bg-teal-500/15 text-teal-300'
                        : 'border-zinc-700/50 bg-zinc-800/30 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                    }`}
                    data-testid={`led-wall-select-${idx}`}
                  >
                    <div className="font-medium">{screen.name || `LED Screen ${idx + 1}`}</div>
                    <div className="text-[9px] opacity-60 mt-0.5">
                      {(() => {
                        const cfg = screen.ledConfig || {};
                        const mode = cfg.ledMode || 'standard';
                        const bc = cfg.columns || 1;
                        const r = cfg.rows || 1;
                        const rH = cfg.tileResH || 128;
                        const rV = cfg.tileResV || 128;
                        const tw = cfg.tileWidth || 500;
                        let ec = bc, cpx = 0;
                        if (mode === 'corner' || mode === 'soft_corner') {
                          const lc = Math.max(1, cfg.leftColumns || Math.ceil(bc / 2));
                          const rc = Math.max(1, cfg.rightColumns || Math.floor(bc / 2));
                          ec = lc + rc;
                          if (mode === 'soft_corner') cpx = Math.round(125 / tw * rH);
                        } else {
                          if (cfg.softCornerLeft) cpx += Math.round(125 / tw * rH);
                          if (cfg.softCornerRight) cpx += Math.round(125 / tw * rH);
                        }
                        return `${ec}x${r} panels | ${rH * ec + cpx}x${rV * r}px`;
                      })()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Detected Config Banner */}
          <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700/50" data-testid="led-config-banner">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Detected LED Config</div>
            <div className="text-xs text-zinc-300 space-y-0.5">
              <div>Panels: <span className="text-teal-400 font-medium">{cols} x {rows} ({totalPanels}{removedCount > 0 ? ` / ${removedCount} removed` : ''})</span></div>
              <div>Resolution: <span className="text-teal-400 font-medium">{totalResW} x {totalResH}px</span></div>
              <div>Physical: <span className="text-teal-400 font-medium">{physW.toFixed(2)}m x {physH.toFixed(2)}m</span></div>
              <div>Panel Size: <span className="text-zinc-400">{tileW}mm x {tileH}mm</span></div>
              <div>Panel Res: <span className="text-zinc-400">{tileResH} x {tileResV} px</span></div>
            </div>
          </div>

          {/* Preview Canvas */}
          <div className="bg-zinc-900 rounded-lg border border-zinc-700/50 overflow-hidden" data-testid="led-preview-area">
            <div className="flex items-center justify-between p-2 border-b border-zinc-700/30">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-zinc-200" onClick={applyToScene} title="Apply to Scene" data-testid="apply-to-scene-btn">
                  <Play className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-zinc-200" onClick={() => { updateSetting('pattern', 'solid'); updateSetting('primaryColor', '#1aa392'); updateSetting('secondaryColor', '#0a0a0a'); }} title="Reset" data-testid="reset-pattern-btn">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" className="h-7 text-[10px] bg-teal-600 hover:bg-teal-700 text-white gap-1" onClick={exportFullPng} data-testid="export-png-btn">
                  <Download className="w-3 h-3" /> Export PNG
                </Button>
              </div>
            </div>
            <div className="p-2 flex justify-center">
              <canvas
                ref={previewRef}
                style={{ width: '100%', maxWidth: previewW, height: 'auto', imageRendering: 'pixelated' }}
                data-testid="led-preview-canvas"
              />
            </div>
          </div>

          {/* Label Settings — right below preview */}
          <div className="space-y-3" data-testid="label-settings-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Label Settings</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="color" value={labelColor} onChange={e => updateSetting('labelColor', e.target.value)}
                  className="w-8 h-8 rounded border border-zinc-700 cursor-pointer bg-transparent" data-testid="label-color-picker" />
                <div>
                  <div className="text-xs text-zinc-300">Text Color</div>
                  <div className="text-[10px] text-zinc-500">{labelColor}</div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-400">Size</span>
                  <span className="text-[10px] text-zinc-500">{labelSize}%</span>
                </div>
                <Slider value={[labelSize]} onValueChange={([v]) => updateSetting('labelSize', v)} min={10} max={150} step={5} className="flex-1" data-testid="label-size-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-400">Horizontal Shift</span>
                  <span className="text-[10px] text-zinc-500">{labelOffsetX}%</span>
                </div>
                <Slider value={[labelOffsetX]} onValueChange={([v]) => updateSetting('labelOffsetX', v)} min={-50} max={50} step={1} className="flex-1" data-testid="label-offset-x-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-400">Vertical Shift</span>
                  <span className="text-[10px] text-zinc-500">{labelOffsetY}%</span>
                </div>
                <Slider value={[labelOffsetY]} onValueChange={([v]) => updateSetting('labelOffsetY', v)} min={-50} max={50} step={1} className="flex-1" data-testid="label-offset-y-slider" />
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Colors */}
          <div className="space-y-3" data-testid="colors-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Colors</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="color" value={primaryColor} onChange={e => updateSetting('primaryColor', e.target.value)}
                  className="w-8 h-8 rounded border border-zinc-700 cursor-pointer bg-transparent" data-testid="primary-color-picker" />
                <div>
                  <div className="text-xs text-zinc-300">Primary Color</div>
                  <div className="text-[10px] text-zinc-500">{primaryColor}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={secondaryColor} onChange={e => updateSetting('secondaryColor', e.target.value)}
                  className="w-8 h-8 rounded border border-zinc-700 cursor-pointer bg-transparent" data-testid="secondary-color-picker" />
                <div>
                  <div className="text-xs text-zinc-300">Secondary Color</div>
                  <div className="text-[10px] text-zinc-500">{secondaryColor}</div>
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Border Width */}
          <div className="space-y-2" data-testid="border-width-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Border Width (px)</div>
            <div className="flex items-center gap-3">
              <Slider
                value={[borderWidth]}
                onValueChange={([v]) => updateSetting('borderWidth', v)}
                min={0} max={10} step={1}
                className="flex-1"
                data-testid="border-width-slider"
              />
              <span className="text-xs text-zinc-400 w-8 text-right">{borderWidth}px</span>
            </div>
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Logo Overlay */}
          <div className="space-y-2" data-testid="logo-overlay-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Logo Overlay</div>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            {logoDataUrl ? (
              <div className="flex items-center gap-2">
                <img src={logoDataUrl} alt="Logo" className="h-8 rounded border border-zinc-700" />
                <Button variant="ghost" size="sm" className="text-xs text-red-400" onClick={() => { setLogoImage(null); setLogoDataUrl(null); setWorldSettings(prev => ({ ...prev, testPatternLogo: null })); }} data-testid="remove-logo-btn">Remove</Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="w-full text-xs border-dashed border-zinc-600 text-zinc-400 hover:text-zinc-200"
                onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-btn">
                <Upload className="w-3 h-3 mr-1" /> Upload Logo
              </Button>
            )}
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Display Options */}
          <div className="space-y-3" data-testid="display-options-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Display Options</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-300">Show Grid</span>
                <Switch checked={showGrid} onCheckedChange={v => updateSetting('showGrid', v)} data-testid="toggle-show-grid" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-300">Show Panel Numbers</span>
                <Switch checked={showNumbers} onCheckedChange={v => updateSetting('showNumbers', v)} data-testid="toggle-show-numbers" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-300">Show Resolution</span>
                <Switch checked={showResolution} onCheckedChange={v => updateSetting('showResolution', v)} data-testid="toggle-show-resolution" />
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Pattern Type — moved below display options */}
          <div className="space-y-2" data-testid="pattern-type-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Pattern Type</div>
            <div className="grid grid-cols-2 gap-1.5">
              {PATTERNS.map(p => (
                <button
                  key={p.id}
                  onClick={() => updateSetting('pattern', p.id)}
                  className={`text-left p-2 rounded-md border transition-colors ${
                    pattern === p.id
                      ? 'border-teal-500 bg-teal-500/15 text-teal-300'
                      : 'border-zinc-700/50 bg-zinc-800/30 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                  }`}
                  data-testid={`pattern-${p.id}`}
                >
                  <div className="text-xs font-medium">{p.label}</div>
                  <div className="text-[9px] opacity-60">{p.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <Separator className="bg-zinc-700/50" />

          {/* Export Actions */}
          <div className="space-y-2" data-testid="export-actions-section">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Export</div>
            <div className="space-y-1.5">
              <Button variant="outline" size="sm" className="w-full text-xs border-zinc-600 text-zinc-300 hover:text-white gap-1.5"
                onClick={exportFullPng} data-testid="export-full-png-btn">
                <Download className="w-3 h-3" /> Export Full PNG ({totalResW}x{totalResH})
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs border-zinc-600 text-zinc-300 hover:text-white gap-1.5"
                onClick={exportAllLedPanelPatterns} data-testid="export-all-patterns-btn">
                <Grid3X3 className="w-3 h-3" /> Export All LED Panel Patterns
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs border-zinc-600 text-zinc-300 hover:text-white gap-1.5"
                onClick={exportPdfReport} data-testid="export-pdf-report-btn">
                <FileText className="w-3 h-3" /> Export LED Report (PDF)
              </Button>
              <Button size="sm" className="w-full text-xs bg-teal-600 hover:bg-teal-700 text-white gap-1.5"
                onClick={applyToScene} data-testid="apply-pattern-to-scene-btn">
                <Play className="w-3 h-3" /> Apply Pattern to LED Wall
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedTestPattern;
